import type { PackForSaleRow } from "@/actions/packs";
import { perBookingMinor, savingAgainst } from "./pricing";

/**
 * One bookable thing, described well enough to ask whether a pack reaches it.
 *
 * Every field is optional because callers know different amounts: an event page
 * has an event and an activity but no chosen pass; a ticket row in the booking
 * sheet has the pass as well. A scope matches on whichever field it names.
 */
export type PackEligibilityLine = {
  eventId?: number | null;
  eventPassTypeId?: number | null;
  venueExperienceId?: number | null;
  venueExperienceTicketId?: number | null;
  activityId?: string | null;
  /** List price of this exact thing, when the caller knows it. */
  listPriceMinor?: number | null;
};

/**
 * Does this pack's scope reach this line?
 *
 * Deliberately the same five-way match `resolvePackCover` does, and deliberately
 * *not* the same function: the engine matches a frozen snapshot to authorise
 * spending a credit, this matches the live catalogue to decide whether to
 * advertise. They answer different questions about different data and must be
 * allowed to differ — an advertisement that is a day stale is a nuisance, a
 * redemption that is a day stale is a wrong charge.
 */
function scopeReaches(
  target: PackForSaleRow["coveredTargets"][number],
  line: PackEligibilityLine,
): boolean {
  switch (target.targetType) {
    case "event":
      return line.eventId != null && line.eventId === target.targetId;
    case "event_pass_type":
      return (
        line.eventPassTypeId != null && line.eventPassTypeId === target.targetId
      );
    case "venue_experience":
      return (
        line.venueExperienceId != null &&
        line.venueExperienceId === target.targetId
      );
    case "venue_experience_ticket":
      return (
        line.venueExperienceTicketId != null &&
        line.venueExperienceTicketId === target.targetId
      );
    case "activity":
      return (
        line.activityId != null &&
        target.activityId != null &&
        line.activityId === target.activityId
      );
    default:
      return false;
  }
}

/** Every published pack of this hiive that pays for this line. */
export function packsCovering(
  packs: PackForSaleRow[],
  line: PackEligibilityLine,
): PackForSaleRow[] {
  return packs.filter((pack) =>
    pack.coveredTargets.some((target) => scopeReaches(target, line)),
  );
}

export type PackEligibility = {
  /** The packs that pay for this line, cheapest per booking first. */
  packs: PackForSaleRow[];
  /**
   * The best saving any of them gives on this exact line.
   *
   * Null when no price was handed in, or when no pack beats paying once. A
   * badge without a number is still worth showing — "a pack covers this" is
   * the discovery, the percentage is the argument.
   */
  bestPercent: number | null;
  /** The cheapest a booking of this line gets, across those packs. */
  bestPerBookingMinor: number | null;
};

/**
 * What to tell somebody looking at this line.
 *
 * The *maximum* saving, not the minimum, and that is the one place in the pack
 * code where the flattering end is the honest one: this is a single known
 * price, so "save up to 46%" names a real pack that really does save 46% on
 * this exact thing. The ranges elsewhere exist because the *line* was unknown,
 * which is not the case here.
 */
export function packEligibilityFor(
  packs: PackForSaleRow[],
  line: PackEligibilityLine,
): PackEligibility | null {
  const covering = packsCovering(packs, line);
  if (covering.length === 0) return null;

  let bestPercent: number | null = null;
  let bestPerBookingMinor: number | null = null;

  for (const pack of covering) {
    const perBooking = perBookingMinor(pack);
    if (perBooking == null) continue;
    if (bestPerBookingMinor == null || perBooking < bestPerBookingMinor) {
      bestPerBookingMinor = perBooking;
    }
    const saving = savingAgainst(perBooking, line.listPriceMinor);
    if (saving && (bestPercent == null || saving.percent > bestPercent)) {
      bestPercent = saving.percent;
    }
  }

  const ordered = [...covering].sort((a, b) => {
    const aPer = perBookingMinor(a) ?? Number.POSITIVE_INFINITY;
    const bPer = perBookingMinor(b) ?? Number.POSITIVE_INFINITY;
    return aPer - bPer;
  });

  return { packs: ordered, bestPercent, bestPerBookingMinor };
}

export type ViewerPackCoverage = {
  packName: string;
  /** Null for a window pack, which meters by time rather than by count. */
  creditsRemaining: number | null;
  /**
   * How the pack reaches this line — the difference between three sentences
   * that are all true of "your pack covers this" and mean different things.
   *
   * - `credit`: spending one of the remaining credits, the ordinary case.
   * - `included`: free, and not out of the balance. The bonus half of a pack.
   * - `booked`: the pack ALREADY paid for this and holds the seat. Said of an
   *   enrolled batch pass on one of its own weekly nights.
   */
  via: "credit" | "included" | "booked";
};

/** A usable holding, reduced to what a badge needs to ask about it. */
export type ViewerPackHolding = Omit<ViewerPackCoverage, "via"> & {
  /** `enrolled` packs book their sessions at purchase rather than on demand. */
  redemptionMode: "on_demand" | "enrolled" | null;
  scopes: Array<{
    targetType: string;
    targetId: number | null;
    activityId: string | null;
    /** Zero means included free — an entitlement, not a credit. */
    creditCost: number;
    /** This scope's own cap is already spent, so it cannot pay again. */
    exhausted: boolean;
  }>;
};

/**
 * The viewer's own pack that reaches this line, if any.
 *
 * Pure, and living here rather than beside its loader for a load-bearing
 * reason: ticket rows call it during render, so it must not drag the server
 * logger — and through it `next/server`'s `after` — into the client bundle.
 * Importing the loader's module from a client component is a build error, not
 * a runtime one, which is at least the good kind.
 *
 * Asks it once per row against holdings loaded a single time; the alternative
 * was a query per row on a screen that renders four of them.
 */
export function holdingCovering(
  holdings: ViewerPackHolding[],
  line: PackEligibilityLine,
): ViewerPackCoverage | null {
  let fallback: ViewerPackCoverage | null = null;

  for (const holding of holdings) {
    const reaching = holding.scopes.filter((scope) => {
      switch (scope.targetType) {
        case "event":
          return line.eventId != null && line.eventId === scope.targetId;
        case "event_pass_type":
          return (
            line.eventPassTypeId != null &&
            line.eventPassTypeId === scope.targetId
          );
        case "venue_experience":
          return (
            line.venueExperienceId != null &&
            line.venueExperienceId === scope.targetId
          );
        case "venue_experience_ticket":
          return (
            line.venueExperienceTicketId != null &&
            line.venueExperienceTicketId === scope.targetId
          );
        case "activity":
          return (
            line.activityId != null &&
            scope.activityId != null &&
            line.activityId === scope.activityId
          );
        default:
          return false;
      }
    });
    if (reaching.length === 0) continue;

    // A scope whose own cap is spent cannot pay again, and saying it can is
    // the failure this guard exists for: an enrolled batch pass reaches every
    // weekly class it booked, so the old scope-reach-only check told a member
    // standing on Tuesday's page that their pass covered it and had a credit
    // left. It does not — `resolvePackCover` refuses with `sub_limit` — and
    // they only found that out at the payment screen, if at all.
    const spendable = reaching.find((scope) => !scope.exhausted);
    if (spendable) {
      return {
        packName: holding.packName,
        creditsRemaining: holding.creditsRemaining,
        via: spendable.creditCost === 0 ? "included" : "credit",
      };
    }

    // Every reaching scope is spent. On an enrolled pack that is not a refusal
    // but the happiest possible answer — the seat is already theirs — and it is
    // worth saying plainly, because the member is on this page precisely
    // because nothing had told them yet.
    fallback ??= {
      packName: holding.packName,
      creditsRemaining: holding.creditsRemaining,
      via: holding.redemptionMode === "enrolled" ? "booked" : "credit",
    };
  }

  return fallback?.via === "booked" ? fallback : null;
}
